'use client'

import React, { useId } from 'react'
import { ArrowUpRight } from 'lucide-react'

import { Media } from '@/components/Media'
import { CMSLink } from '@/components/Link'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { cn } from '@/utilities/ui'
import SectionHeader from '@/components/ui/SectionHeader'
import RichText from '@/components/RichText'
import { formatOpeningHours, type OpeningHoursSlot } from '@/lib/locations'
import type { ResolvedLocation } from '@/utilities/getCompanyLocations'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

interface LocationItemProps {
  image: any
  imagePosition?: 'left' | 'right'
  bleed?: boolean
  infoTagText?: string
  title: string
  description?: DefaultTypedEditorState | null
  links?: Array<{ link: any }>
  dark?: boolean
  /** Resolved Global entry — the authoritative source for address / phone / hours. */
  location: ResolvedLocation
}

export const LocationItem: React.FC<LocationItemProps> = ({
  image,
  imagePosition = 'right',
  bleed = false,
  infoTagText,
  title,
  description,
  links,
  dark = false,
  location,
}) => {
  const imageContainerId = useId()
  const isImageLeft = imagePosition === 'left'

  // On mobile and tablet, keep image over text; on lg+, respect side.
  const imageOrder = isImageLeft ? 'order-1' : 'order-1 lg:order-2'
  const textOrder = isImageLeft ? 'order-2' : 'order-2 lg:order-1'
  const textAlignClasses = isImageLeft
    ? 'text-left items-start lg:text-left lg:items-start'
    : 'text-left items-start lg:text-right lg:items-end'

  // Bleed only the image to the outside viewport edge on large screens
  // On mobile, use normal layout without bleed
  const bleedStyle = (() => {
    if (!bleed) return undefined
    const amount = 'calc(50vw - 50%)'
    return isImageLeft
      ? {
          marginLeft: `calc(-1 * (${amount}))`,
          width: `calc(100% + (${amount}))`,
        }
      : {
          marginRight: `calc(-1 * (${amount}))`,
          width: `calc(100% + (${amount}))`,
        }
  })()

  return (
    <div className="overflow-hidden">
      {/* Style tag to apply bleed only on desktop */}
      {bleed && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
            #${imageContainerId} {
              margin-left: 0;
              margin-right: 0;
              width: 100%;
            }
            @media (min-width: 1024px) {
              #${imageContainerId} {
                ${isImageLeft ? `margin-left: ${bleedStyle?.marginLeft};` : `margin-right: ${bleedStyle?.marginRight};`}
                width: ${bleedStyle?.width};
              }
            }
          `,
          }}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-16 items-stretch xl:items-center min-h-[360px] lg:min-h-[490px] xl:min-h-0">
        {/* Image */}
        <div className={cn(imageOrder, 'lg:col-span-2 xl:h-auto xl:aspect-[21/9]')}>
          {/* Mobile: normal full-width layout */}
          {/* Desktop: apply bleed effect if enabled */}
          <div id={imageContainerId} className="relative w-full h-full min-h-[360px] xl:min-h-0">
            <MotionFadeIn className="relative w-full h-full min-h-[360px] xl:min-h-0">
              {image && typeof image === 'object' && (
                <Media
                  resource={image}
                  fill
                  pictureClassName="absolute inset-0 w-full h-full"
                  imgClassName="object-cover"
                  size={
                    bleed
                      ? '(max-width: 1024px) 100vw, 75vw'
                      : '(max-width: 1024px) 100vw, 66vw'
                  }
                />
              )}
            </MotionFadeIn>
          </div>
        </div>

        {/* Text */}
        <div
          className={cn(
            textOrder,
            'lg:col-span-1 py-5 lg:py-10 container mx-auto px-8 lg:max-w-none lg:mx-0 lg:px-0',
            'flex flex-col lg:justify-center',
            isImageLeft ? 'lg:items-start' : 'lg:items-end',
          )}
        >
          <MotionFadeIn
            initialY={20}
            duration={0.7}
            className={cn(
              'flex flex-col gap-4',
              textAlignClasses,
              'md:max-w-[616px] w-full',
            )}
          >
            <SectionHeader
              infoTagText={infoTagText ?? undefined}
              title={title}
              size="sm"
              className={cn('mb-0 w-full', !isImageLeft && 'lg:text-right')}
              titleClassName={cn(
                dark ? 'on-dark' : 'text-primary',
                !isImageLeft && 'lg:text-right',
              )}
            >
              <div
                className={cn(
                  'pt-4 body-base flex flex-col gap-1',
                  dark ? 'on-dark-secondary' : 'text-secondary',
                  !isImageLeft && 'lg:items-end',
                )}
              >
                <p>{location.streetAddress}</p>
                <p>
                  CH-{location.postalCode} {location.addressLocality}
                </p>
                <p className="pt-3">
                  Telefon{' '}
                  <a
                    href={`tel:${location.telephone.replace(/\s/g, '')}`}
                    className="text-text-brand hover:opacity-80"
                  >
                    {location.telephone}
                  </a>
                </p>
                <p>
                  24H Hotline{' '}
                  <a
                    href={`tel:${location.hotline.replace(/\s/g, '')}`}
                    className="text-text-brand hover:opacity-80"
                  >
                    {location.hotline}
                  </a>
                </p>
                <p className="pt-3">
                  <a
                    href={`mailto:${location.email}`}
                    className="text-text-brand hover:opacity-80"
                  >
                    {location.email}
                  </a>
                </p>
              </div>
              {Array.isArray(location.openingHours) && location.openingHours.length > 0 && (
                <div
                  className={cn(
                    'pt-4 body-base',
                    dark ? 'on-dark-secondary' : 'text-secondary',
                    !isImageLeft && 'lg:text-right',
                  )}
                >
                  <p className="text-sm tracking-widest uppercase opacity-70 mb-2">
                    Öffnungszeiten
                  </p>
                  <ul className="space-y-1">
                    {formatOpeningHours(location.openingHours as OpeningHoursSlot[]).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                    <li className="text-sm opacity-70 pt-1">
                      Notfälle 24/7 über die Hotline
                    </li>
                  </ul>
                </div>
              )}
              {description && (
                <RichText
                  data={description}
                  enableGutter={false}
                  enableProse={false}
                  className={cn(
                    'body-base pt-4',
                    dark ? 'on-dark-secondary' : 'text-secondary',
                    !isImageLeft && 'lg:text-right',
                  )}
                />
              )}
            </SectionHeader>
            {links && links.length > 0 && links[0]?.link && (
              <div className={cn('mt-6 flex', isImageLeft ? 'justify-start' : 'justify-end')}>
                <CMSLink
                  {...links[0].link}
                  newTab={links[0].link.openInNewTab}
                  appearance="inline"
                  label={null}
                  className="group inline-flex items-center gap-4"
                >
                  <span className="text-md font-ui font-medium text-brand transition-opacity duration-200 uppercase group-hover:opacity-80">
                    {links[0].link.label}
                  </span>
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center shrink-0 w-12 h-12 bg-brand text-white"
                  >
                    <ArrowUpRight
                      className={cn(
                        'w-6 h-6 transition-transform duration-300',
                        links[0].link.openInNewTab
                          ? 'rotate-45 group-hover:-rotate-90'
                          : 'rotate-90 group-hover:rotate-45',
                      )}
                    />
                  </span>
                </CMSLink>
              </div>
            )}
          </MotionFadeIn>
        </div>
      </div>
    </div>
  )
}
